import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { uploadBlobThroughStorage } from 'shared-ui';
import { API_BASE_URL, BUSINESS_ID } from '../config/api';

const API_BASE = API_BASE_URL;
const CHAT_BASE = `${API_BASE}/event-chat`;
const STRIPE_PUBLISHABLE_KEY_FALLBACK = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();

const TAB_DEFS = [
  { id: 'overview', ic: '🌊', label: 'Overview' },
  { id: 'organisers', ic: '👥', label: 'Organisers' },
  { id: 'addons', ic: '🍺', label: 'Add-ons' },
  { id: 'gear', ic: '🏕', label: 'Gear' },
  { id: 'food', ic: '🍉', label: 'Food' },
  { id: 'gallery', ic: '📸', label: 'Gallery' },
  { id: 'mystuff', ic: '🎒', label: 'My stuff' },
  { id: 'chat', ic: '💬', label: 'Chat' },
];

async function fetchStripePublishableKey() {
  try {
    const res = await fetch(`${API_BASE}/business/stripe-public${BUSINESS_ID ? `?businessId=${encodeURIComponent(BUSINESS_ID)}` : ''}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return (data.publishableKey || '').trim();
  } catch (err) {
    console.warn('Could not fetch Stripe key, using env fallback:', err.message);
    return STRIPE_PUBLISHABLE_KEY_FALLBACK;
  }
}

function CardSetupForm({ onSaved, saving, setSaving, setError, customer, submitLabel, savingLabel }) {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError(null);
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });
      if (error) {
        setError(error.message || 'Could not save your card');
        setSaving(false);
        return;
      }
      onSaved(setupIntent.payment_method);
    } catch (err) {
      setError(err.message || 'Could not save your card');
      setSaving(false);
    }
  };

  // Prefills Stripe's own "save for faster checkout" (Link) fields with what we already know
  // about the logged-in customer, so they aren't retyping their name/email/phone.
  const defaultValues = {
    billingDetails: {
      name: [customer?.firstName, customer?.lastName].filter(Boolean).join(' ') || undefined,
      email: customer?.email || undefined,
      phone: customer?.phone || undefined,
    },
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ defaultValues }} />
      <button type="submit" disabled={!stripe || saving} style={{ marginTop: 16, padding: '12px 24px', fontSize: 16, cursor: 'pointer' }}>
        {saving ? (savingLabel || 'Saving...') : (submitLabel || "Save card — you won't be charged yet")}
      </button>
    </form>
  );
}

// ---- shared inline style tokens for the Command Deck ----
const deckTabsWrap = { display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6, marginBottom: 16, WebkitOverflowScrolling: 'touch' };
const deckTabBtn = (on) => ({
  flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
  background: on ? '#eef2ff' : '#fff', border: `1px solid ${on ? '#818cf8' : '#e0e0e0'}`, borderRadius: 12,
  padding: '9px 13px', cursor: 'pointer', minWidth: 70,
});
const deckTabIcon = { fontSize: 17, lineHeight: 1 };
const deckTabLabel = (on) => ({ fontSize: 10.5, fontWeight: 600, color: on ? '#3730a3' : '#666' });
const deckTabBadge = { fontSize: 9.5, color: '#999' };
const deckWell = { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 16, minHeight: 160 };
const sectionHeading = { margin: '0 0 4px', fontSize: 16, fontWeight: 700 };
const sectionSub = { fontSize: 13, color: '#888', margin: '0 0 12px' };
const cardStyle = { padding: 12, marginBottom: 8, background: '#f8f9fa', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 14 };

export default function EventSignup() {
  const { eventSlug } = useParams();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || '';
  const codeFromUrl = searchParams.get('code') || '';
  const { customer, isAuthenticated, getAuthHeaders } = useCustomerAuth();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPrivateLocked, setIsPrivateLocked] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const [savingCard, setSavingCard] = useState(false);
  const [signupResult, setSignupResult] = useState(null); // { data, waitlisted }
  const [activeTab, setActiveTab] = useState('overview');
  const [myReferralLink, setMyReferralLink] = useState(null);
  const [addOns, setAddOns] = useState([]);
  const [chatTopics, setChatTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState(null); // null = "All" (unified stream)
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoadError, setChatLoadError] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatPhotoFile, setChatPhotoFile] = useState(null);
  const [chatItemName, setChatItemName] = useState('');
  const [showItemOffer, setShowItemOffer] = useState(false);
  const [sendingChat, setSendingChat] = useState(false);
  const [roles, setRoles] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [newNeedName, setNewNeedName] = useState('');
  const [newCarName, setNewCarName] = useState('');
  const [newTentName, setNewTentName] = useState('');
  const [addingNeed, setAddingNeed] = useState(false);
  const [offeringItemId, setOfferingItemId] = useState(null);
  const [offerPhotoFile, setOfferPhotoFile] = useState(null);
  const [offeringBusy, setOfferingBusy] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [galleryUploadFile, setGalleryUploadFile] = useState(null);
  const [galleryCaption, setGalleryCaption] = useState('');
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [dietaryPreference, setDietaryPreference] = useState('');
  const [venueTiers, setVenueTiers] = useState([]);
  const [carCount, setCarCount] = useState(1);
  const [partySize, setPartySize] = useState(1);
  const [emergencyContactName, setEmergencyContactName] = useState('');
  const [emergencyContactPhone, setEmergencyContactPhone] = useState('');
  const [proposedAddOnForm, setProposedAddOnForm] = useState({ name: '', unitCost: '', unitYield: '', minSharePerPerson: '', unitYieldLabel: '' });
  const [proposingAddOn, setProposingAddOn] = useState(false);
  const [retryClientSecret, setRetryClientSecret] = useState(null);
  const [retryStripeCustomerId, setRetryStripeCustomerId] = useState(null);
  const [retrySaving, setRetrySaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [updatingDietary, setUpdatingDietary] = useState(false);
  const [expandedAddOnId, setExpandedAddOnId] = useState(null);

  const uploadFile = (file, meta) => uploadBlobThroughStorage({ apiBaseUrl: API_BASE, tokenKey: 'customer_token', file, meta });

  const isDeferred = event?.paymentCaptureMode === 'deferred_threshold';
  const isImmediate = event?.paymentCaptureMode === 'immediate_on_signup';
  const isCarTiers = event?.venueSelectionMode === 'car_tiers';
  const hasCarPersonPricing = event?.pricePerCar != null || event?.pricePerPerson != null;
  const collectsCarPerson = isCarTiers || hasCarPersonPricing;
  const overThreshold = event?.thresholdReachedAt != null;
  const currentPerPersonRate = overThreshold && event?.pricePerPersonAtThreshold != null ? event.pricePerPersonAtThreshold : event?.pricePerPerson;
  // Once the threshold is hit, the car fee drops entirely — flat per-person rate.
  const carTierPricePreview = collectsCarPerson
    ? overThreshold && event?.pricePerPersonAtThreshold != null
      ? (currentPerPersonRate || 0) * partySize
      : (currentPerPersonRate || 0) * partySize + (event?.pricePerCar || 0) * carCount
    : null;

  const loadEvent = async (overrideCode) => {
    setLoading(true);
    setIsPrivateLocked(false);
    try {
      const codeToUse = overrideCode ?? codeFromUrl ?? manualCode;
      const qs = new URLSearchParams();
      if (codeToUse) qs.set('code', codeToUse);
      if (referralCode) qs.set('ref', referralCode);
      if (customer?.id) qs.set('customerId', customer.id);
      const res = await fetch(`${API_BASE}/events/public/${eventSlug}${qs.toString() ? `?${qs.toString()}` : ''}`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (res.status === 403 && json.error === 'private_event') {
        setIsPrivateLocked(true);
        return;
      }
      if (!res.ok) throw new Error(json.error || 'Event not found');
      setEvent(json.event);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventSlug]);

  // If the initial anonymous load hit the invite-only lock, retry once customer auth resolves —
  // an attendee who already has a ticket shouldn't need the code again just because auth loaded
  // after the first fetch (e.g. arriving here from a 'My Events' link with no code in the URL).
  useEffect(() => {
    if (isPrivateLocked && customer?.id) {
      loadEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivateLocked, customer?.id]);

  const handleUnlockWithCode = (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    loadEvent(manualCode.trim());
  };

  useEffect(() => {
    fetchStripePublishableKey().then((key) => {
      if (key) setStripePromise(loadStripe(key));
    });
  }, []);

  const startCardSetup = async () => {
    if (!event || !customer) return;
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not start signup');
      setClientSecret(json.data.clientSecret);
      setStripeCustomerId(json.data.stripeCustomerId);
    } catch (err) {
      setError(err.message);
    }
  };

  const completeSignup = async (stripePaymentMethodId) => {
    if (event.collectEmergencyContact && (!emergencyContactName.trim() || !emergencyContactPhone.trim())) {
      setError('Emergency contact name and phone are required for this event');
      setSavingCard(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          customerId: customer.id,
          stripeCustomerId,
          stripePaymentMethodId,
          referralCode: referralCode || undefined,
          ...(collectsCarPerson ? { carCount, partySize } : {}),
          ...(event.collectEmergencyContact ? { emergencyContactName: emergencyContactName.trim(), emergencyContactPhone: emergencyContactPhone.trim() } : {}),
          ...(dietaryPreference ? { dietaryPreference } : {}),
        }),
      });
      const json = await res.json();
      // A declined immediate charge (402) still creates the ticket (status payment_failed) — land
      // on the normal post-signup view so My Stuff's "update card and retry" flow is reachable,
      // instead of just showing an error and leaving them stuck on the signup form.
      if (!json.data) throw new Error(json.error || 'Signup failed');
      setSignupResult(json);
      if (!json.success && json.error) setError(json.error);
      loadEvent();
      loadMyReferralLink();
      loadAddOns();
    } catch (err) {
      setError(err.message);
      setSavingCard(false);
    }
  };

  const loadMyReferralLink = async () => {
    if (!customer) return;
    try {
      const res = await fetch(`${API_BASE}/referrals/my-code?customerId=${customer.id}`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (json.shareUrl) {
        const eventUrl = `${window.location.origin}/events/${eventSlug}?ref=${json.code}`;
        setMyReferralLink(eventUrl);
      }
    } catch (err) {
      console.warn('Could not load referral code:', err.message);
    }
  };

  const loadAddOns = async () => {
    if (!event) return;
    try {
      const qs = customer ? `?customerId=${customer.id}` : '';
      const res = await fetch(`${API_BASE}/events/${event.id}/addons${qs}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setAddOns(json.data || []);
    } catch (err) {
      console.warn('Could not load add-ons:', err.message);
    }
  };

  const loadVenueTiers = async () => {
    if (!event) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/venue-tiers`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setVenueTiers(json.data || []);
    } catch (err) {
      console.warn('Could not load venue tiers:', err.message);
    }
  };

  useEffect(() => {
    if (event?.id && isAuthenticated && isCarTiers) loadVenueTiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, isAuthenticated]);

  const loadMyTicket = async () => {
    if (!event || !customer) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/tickets/my?customerId=${customer.id}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success && json.data) setSignupResult({ data: json.data, waitlisted: false });
    } catch (err) {
      console.warn('Could not load existing ticket:', err.message);
    }
  };

  useEffect(() => {
    if (event?.id && isAuthenticated && !signupResult) loadMyTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, isAuthenticated]);

  const startPaymentRetry = async () => {
    if (!event || !customer) return;
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not start retry');
      setRetryClientSecret(json.data.clientSecret);
      setRetryStripeCustomerId(json.data.stripeCustomerId);
    } catch (err) {
      setError(err.message);
    }
  };

  const completeRetry = async (stripePaymentMethodId) => {
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/tickets/retry-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id, stripeCustomerId: retryStripeCustomerId, stripePaymentMethodId }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Payment failed');
      setSignupResult({ data: json.data, waitlisted: false });
      setRetryClientSecret(null);
      setRetryStripeCustomerId(null);
    } catch (err) {
      setError(err.message);
      setRetrySaving(false);
    }
  };

  const handleCancelSignup = async () => {
    if (!event || !customer) return;
    if (!window.confirm("Cancel your spot? You'll need to sign up again if you change your mind.")) return;
    setCancelling(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/tickets/my?customerId=${customer.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not cancel');
      setSignupResult(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const handleUpdateDietary = async (pref) => {
    if (!event || !customer) return;
    setUpdatingDietary(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/tickets/my?customerId=${customer.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id, dietaryPreference: pref }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not update');
      setSignupResult((prev) => (prev ? { ...prev, data: { ...prev.data, dietaryPreference: json.data.dietaryPreference } } : prev));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingDietary(false);
    }
  };

  const handleProposeAddOn = async () => {
    if (!event || !customer) return;
    const { name, unitCost, unitYield, minSharePerPerson, unitYieldLabel } = proposedAddOnForm;
    if (!name || !unitCost || !minSharePerPerson) {
      setError('Name, unit cost, and minimum share per person are required');
      return;
    }
    setProposingAddOn(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/addons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          customerId: customer.id,
          name,
          unitCost: Number(unitCost),
          unitYield: unitYield ? Number(unitYield) : undefined,
          unitYieldLabel: unitYieldLabel || undefined,
          minSharePerPerson: Number(minSharePerPerson),
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not propose add-on');
      setProposedAddOnForm({ name: '', unitCost: '', unitYield: '', minSharePerPerson: '', unitYieldLabel: '' });
      loadAddOns();
    } catch (err) {
      setError(err.message);
    } finally {
      setProposingAddOn(false);
    }
  };

  const loadChatMessages = async (topicId) => {
    if (!event) return;
    try {
      const qs = topicId ? `?topicId=${topicId}` : '';
      const res = await fetch(`${CHAT_BASE}/${event.id}/messages${qs}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) {
        setChatMessages(json.data || []);
        setChatLoadError(null);
      } else {
        setChatLoadError(json.error || 'Could not load chat');
      }
    } catch (err) {
      setChatLoadError(err.message || 'Could not load chat');
    }
  };

  const loadChatTopics = async () => {
    if (!event) return;
    try {
      const res = await fetch(`${CHAT_BASE}/${event.id}/topics`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setChatTopics(json.data || []);
    } catch (err) {
      console.warn('Could not load chat topics:', err.message);
    }
  };

  const loadRoles = async () => {
    if (!event) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/roles`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setRoles(json.data || []);
    } catch (err) {
      console.warn('Could not load roles:', err.message);
    }
  };

  const loadInventory = async () => {
    if (!event) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/inventory`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setInventoryItems(json.data || []);
    } catch (err) {
      console.warn('Could not load inventory:', err.message);
    }
  };

  const loadGallery = async () => {
    if (!event) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/gallery`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setGalleryPhotos(json.data || []);
    } catch (err) {
      console.warn('Could not load gallery:', err.message);
    }
  };

  const handleUploadGalleryPhoto = async () => {
    if (!event || !customer || !galleryUploadFile) return;
    setGalleryUploading(true);
    setError(null);
    try {
      const uploaded = await uploadFile(galleryUploadFile, { fileType: 'ATTACHMENT' });
      const res = await fetch(`${API_BASE}/events/${event.id}/gallery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id, photoUrl: uploaded.url, caption: galleryCaption.trim() || undefined }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not post to the gallery');
      setGalleryUploadFile(null);
      setGalleryCaption('');
      loadGallery();
    } catch (err) {
      setError(err.message);
    } finally {
      setGalleryUploading(false);
    }
  };

  const handleDeleteGalleryPhoto = async (photoId) => {
    if (!event || !customer) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/gallery/${photoId}?customerId=${customer.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not remove');
      loadGallery();
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (signupResult) {
      loadMyReferralLink();
      loadAddOns();
      loadRoles();
      loadInventory();
      loadGallery();
      loadChatTopics();
      loadChatMessages(selectedTopicId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signupResult?.data?.id]);

  useEffect(() => {
    if (signupResult) loadChatMessages(selectedTopicId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicId]);

  const handleSendChatMessage = async () => {
    if (!chatInput.trim() && !chatPhotoFile) return;
    setSendingChat(true);
    setError(null);
    try {
      let photoUrl = null;
      if (chatPhotoFile) {
        const uploaded = await uploadFile(chatPhotoFile, { fileType: 'ATTACHMENT' });
        photoUrl = uploaded.url;
      }
      const res = await fetch(`${CHAT_BASE}/${event.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          content: chatInput.trim() || `Offering: ${chatItemName}`,
          topicId: selectedTopicId || undefined,
          photoUrl,
          inventoryItemName: showItemOffer ? chatItemName || undefined : undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not send message');
      setChatInput('');
      setChatPhotoFile(null);
      setChatItemName('');
      setShowItemOffer(false);
      loadChatMessages(selectedTopicId);
      if (json.data?.linkedInventoryItemId) loadInventory();
    } catch (err) {
      setError(err.message);
    } finally {
      setSendingChat(false);
    }
  };

  const handleClaimLeader = async (roleId) => {
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/roles/${roleId}/claim-leader`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not claim role');
      loadRoles();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleJoinRole = async (roleId) => {
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/roles/${roleId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not join role');
      loadRoles();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLeaveRole = async (roleId) => {
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/roles/${roleId}/leave?customerId=${customer.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not leave role');
      loadRoles();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddNeed = async (name, category, onDone) => {
    if (!name.trim()) return;
    setAddingNeed(true);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/inventory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ name: name.trim(), category }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not add need');
      onDone?.();
      loadInventory();
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingNeed(false);
    }
  };

  const handleOfferOnNeed = async (itemId) => {
    if (!customer) return;
    setOfferingBusy(true);
    setError(null);
    try {
      let photoUrl = null;
      if (offerPhotoFile) {
        const uploaded = await uploadFile(offerPhotoFile, { fileType: 'ATTACHMENT' });
        photoUrl = uploaded.url;
      }
      const res = await fetch(`${API_BASE}/events/${event.id}/inventory/${itemId}/offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id, photoUrl }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not offer item');
      setOfferingItemId(null);
      setOfferPhotoFile(null);
      loadInventory();
    } catch (err) {
      setError(err.message);
    } finally {
      setOfferingBusy(false);
    }
  };

  const handleWithdrawOffer = async (itemId) => {
    if (!customer) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/inventory/${itemId}/offer?customerId=${customer.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not withdraw offer');
      loadInventory();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddOnSignup = async (addOnId, quantity = 1) => {
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/addons/${addOnId}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id, quantity }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not join add-on');
      loadAddOns();
    } catch (err) {
      setError(err.message);
    }
  };

  const elementsOptions = useMemo(() => (clientSecret ? { clientSecret } : null), [clientSecret]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (isPrivateLocked) {
    return (
      <div style={{ maxWidth: 420, margin: '0 auto', padding: '60px 20px', textAlign: 'center' }}>
        <h2>This is an invite-only event</h2>
        <p style={{ color: '#666' }}>Ask the organizer for your invite link, or enter your code below.</p>
        <form onSubmit={handleUnlockWithCode} style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <input
            placeholder="Invite code"
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
          />
          <button type="submit" style={{ padding: '10px 16px', cursor: 'pointer' }}>Unlock</button>
        </form>
      </div>
    );
  }
  if (error && !event) return <div style={{ padding: 40, textAlign: 'center', color: 'crimson' }}>{error}</div>;
  if (!event) return null;

  const progress = event.progress;
  const gearItems = inventoryItems.filter((it) => (it.category || 'gear') === 'gear' || it.category === 'other');
  const foodItems = inventoryItems.filter((it) => it.category === 'snacks');
  const carItems = inventoryItems.filter((it) => it.category === 'car');
  const tentItems = inventoryItems.filter((it) => it.category === 'tent');
  const unclaimedRolesCount = roles.filter((r) => !r.leader).length;
  const activeAddOns = addOns.filter((a) => a.status === 'active');
  const openGearCount = gearItems.filter((it) => it.status !== 'confirmed').length;
  const openFoodCount = foodItems.filter((it) => it.status !== 'confirmed').length;
  const myOfferedItems = inventoryItems.filter((it) => it.offers?.some((o) => o.customer?.id === customer?.id));
  const myAddOns = addOns.filter((a) => a.participants?.some((p) => p.customerId === customer?.id));

  const TAB_BADGES = {
    organisers: unclaimedRolesCount > 0 ? `${unclaimedRolesCount} open` : null,
    addons: activeAddOns.length > 0 ? String(activeAddOns.length) : null,
    gear: openGearCount > 0 ? String(openGearCount) : null,
    food: openFoodCount > 0 ? String(openFoodCount) : null,
    gallery: galleryPhotos.length > 0 ? String(galleryPhotos.length) : null,
    chat: chatMessages.length > 0 ? String(chatMessages.length) : null,
  };

  // -------------------- shared render helpers (used inside tab panels) --------------------

  const renderOverviewInfo = () => (
    <>
      {event.coverImage && (
        <img src={event.coverImage} alt={event.name} style={{ width: '100%', borderRadius: 12, marginBottom: 24 }} />
      )}
      {event.description && <p style={{ whiteSpace: 'pre-wrap' }}>{event.description}</p>}
      {event.videoUrl && (
        <video src={event.videoUrl} controls style={{ width: '100%', borderRadius: 12, marginBottom: 24 }} />
      )}
      {event.scheduleText && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 6 }}>Schedule</h3>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{event.scheduleText}</p>
        </div>
      )}
      {event.safetyNote && (
        <div style={{ marginBottom: 20, padding: 14, background: '#fff8e1', borderRadius: 8, border: '1px solid #ffe082' }}>
          <h3 style={{ marginTop: 0, marginBottom: 6 }}>Safety</h3>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{event.safetyNote}</p>
        </div>
      )}
      {event.digitalFileUrl && (
        <p style={{ marginBottom: 20 }}>
          <a href={event.digitalFileUrl} target="_blank" rel="noreferrer">📍 Site map / arrival instructions</a>
        </p>
      )}
      {isCarTiers && (
        <div style={{ margin: '20px 0', padding: 16, background: '#f6fbff', borderRadius: 8, border: '1px solid #dbeafe' }}>
          <strong>Venue: we'll book the site that fits everyone</strong>
          {event.venueSelectionDate && (
            <p style={{ margin: '6px 0', fontSize: 14 }}>
              Decided on {new Date(event.venueSelectionDate).toLocaleDateString()} based on confirmed cars.
            </p>
          )}
          {venueTiers.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 14 }}>
              {venueTiers.map((tier) => (
                <li key={tier.id} style={{ fontWeight: event.selectedVenueTierId === tier.id ? 700 : 400 }}>
                  {tier.name} — up to {tier.maxCars} cars{tier.location ? ` (${tier.location})` : ''}
                  {event.selectedVenueTierId === tier.id ? ' ✅ selected' : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );

  const renderStatusStrip = () => (
    progress && (
      <div style={{ margin: '20px 0', padding: 16, background: '#f6fbff', borderRadius: 8, border: '1px solid #dbeafe' }}>
        <strong>{progress.confirmedCount} confirmed</strong>
        {event.maxCapacity ? ` (max ${event.maxCapacity})` : ''}
        {event.maxCapacity && (
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#666' }}>
            Once we hit {event.maxCapacity} people we'll close signups — anyone after that goes on the list for our next one.
          </p>
        )}
        {progress.waitlistCount > 0 && (
          <div style={{ margin: '8px 0 0' }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
              {progress.waitlistCount} {progress.waitlistCount === 1 ? 'person has' : 'people have'} signed up for the next event.
            </p>
            {progress.waitlistNames?.length > 0 && (
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#666' }}>
                On the list: {progress.waitlistNames.join(', ')}
              </p>
            )}
          </div>
        )}
        {progress.minAttendeesToConfirm != null && progress.confirmedCount < progress.minAttendeesToConfirm && (
          <p style={{ margin: '8px 0 0', fontSize: 14 }}>
            We need {progress.minAttendeesToConfirm - progress.confirmedCount} more to lock the site in.
          </p>
        )}
        {hasCarPersonPricing && (
          <p style={{ margin: '8px 0 0', fontSize: 14 }}>
            {isImmediate ? (
              <>${currentPerPersonRate ?? 0}/person — covers the camping fee and food for the weekend.</>
            ) : overThreshold && event.pricePerPersonAtThreshold != null ? (
              <>
                Current rate: ${event.pricePerPersonAtThreshold}/person (no car fee) — locked in, we hit {event.chargeThresholdAttendees}.
                {' '}
                {event.chargeBatchTriggeredAt
                  ? 'You were notified, and cards have been charged.'
                  : event.thresholdReachedAt
                    ? `You've been notified — cards will be charged ${new Date(new Date(event.thresholdReachedAt).getTime() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString()}.`
                    : ''}
              </>
            ) : (
              <>
                Current rate: ${event.pricePerCar ?? 0}/car + ${currentPerPersonRate ?? 0}/person
                {event.chargeThresholdAttendees && event.pricePerPersonAtThreshold != null && (
                  <> — drops the car fee and jumps to a flat ${event.pricePerPersonAtThreshold}/person for everyone once we hit {event.chargeThresholdAttendees}. You'll be notified the moment that happens, then charged 3 days later.</>
                )}
              </>
            )}
            {!isImmediate && event.registrationDeadline && (
              <> · nobody's charged until {new Date(event.registrationDeadline).toLocaleDateString()} or we hit {event.chargeThresholdAttendees}, whichever comes first</>
            )}
          </p>
        )}
      </div>
    )
  );

  const renderNeedOfferBoard = (items, category, emptyLabel, offerLabel = 'I have this', confirmLabel = 'Confirm offer', addPlaceholder, nameValue = newNeedName, onNameChange = setNewNeedName) => (
    <>
      {items.length === 0 ? (
        <p style={{ fontSize: 14, color: '#888' }}>{emptyLabel}</p>
      ) : (
        items.map((item) => {
          const myOffer = item.offers?.find((o) => o.customer?.id === customer?.id);
          return (
            <div key={item.id} style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong>{item.name}</strong>
                {!myOffer ? (
                  <button onClick={() => setOfferingItemId(item.id)} style={{ padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}>
                    {offerLabel}
                  </button>
                ) : (
                  <button onClick={() => handleWithdrawOffer(item.id)} style={{ padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}>
                    Withdraw
                  </button>
                )}
              </div>
              {item.offers?.length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {item.offers.map((o) => (
                    <div key={o.id} style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 4 }}>
                      {o.photoUrl && <img src={o.photoUrl} alt="" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4 }} />}
                      {o.customer?.firstName} {o.status === 'confirmed' ? '✅' : o.status === 'declined' ? '❌' : ''}
                    </div>
                  ))}
                </div>
              )}
              {offeringItemId === item.id && (
                <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="file" accept="image/*" onChange={(e) => setOfferPhotoFile(e.target.files?.[0] || null)} style={{ fontSize: 12 }} />
                  <button onClick={() => handleOfferOnNeed(item.id)} disabled={offeringBusy} style={{ padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}>
                    {offeringBusy ? 'Saving...' : confirmLabel}
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <input
          placeholder={addPlaceholder || (category === 'snacks' ? 'Add to the shopping list... (e.g. alfajores)' : 'Add something needed... (e.g. a big pot)')}
          value={nameValue}
          onChange={(e) => onNameChange(e.target.value)}
          style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
        />
        <button onClick={() => handleAddNeed(nameValue, category, () => onNameChange(''))} disabled={addingNeed} style={{ padding: '10px 16px', cursor: 'pointer' }}>
          {addingNeed ? 'Adding...' : 'Add'}
        </button>
      </div>
    </>
  );

  // Food is a pure wishlist — the $60 ticket already covers food, so this isn't asking
  // attendees to bring/fulfill anything, just to say what they'd like to eat.
  const renderWishlistBoard = (items, category, emptyLabel) => (
    <>
      {items.length === 0 ? (
        <p style={{ fontSize: 14, color: '#888' }}>{emptyLabel}</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {items.map((item) => (
            <span key={item.id} style={{ padding: '6px 12px', background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 20, fontSize: 13 }}>
              {item.name}
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <input
          placeholder="What do you want to eat at camp? (e.g. alfajores)"
          value={newNeedName}
          onChange={(e) => setNewNeedName(e.target.value)}
          style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
        />
        <button onClick={() => handleAddNeed(newNeedName, category, () => setNewNeedName(''))} disabled={addingNeed} style={{ padding: '10px 16px', cursor: 'pointer' }}>
          {addingNeed ? 'Adding...' : 'Add'}
        </button>
      </div>
    </>
  );

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '40px 20px' }}>
      <p style={{ margin: '0 0 4px', fontSize: 13, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Your opportunity to be a part of One Onda
      </p>
      <h1 style={{ margin: 0 }}>{event.name}</h1>
      {event.date && <p>{new Date(event.date).toLocaleString()}</p>}
      {event.location && <p>{event.location}</p>}

      {renderStatusStrip()}

      {error && <div style={{ color: 'crimson', margin: '12px 0' }}>{error}</div>}

      {!signupResult ? (
        <>
          {renderOverviewInfo()}

          {!isAuthenticated ? (
            <p>
              <Link to="/customer/login">Log in</Link> or <Link to="/customer/signup">create an account</Link> to sign up for this event.
            </p>
          ) : !isDeferred && !isCarTiers && !isImmediate ? (
            <p>Ticketing for this event isn't set up for online signup yet — please contact us.</p>
          ) : !clientSecret ? (
            <>
              {collectsCarPerson && (
                <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                  <label>
                    Cars<br />
                    <input type="number" min={0} value={carCount} onChange={(e) => setCarCount(Math.max(0, Number(e.target.value) || 0))} style={{ width: 80, padding: 8, borderRadius: 6, border: '1px solid #ccc' }} />
                    <span style={{ display: 'block', fontSize: 11, color: '#888' }}>0 if riding with someone</span>
                  </label>
                  <label>
                    People<br />
                    <input type="number" min={1} value={partySize} onChange={(e) => setPartySize(Math.max(1, Number(e.target.value) || 1))} style={{ width: 80, padding: 8, borderRadius: 6, border: '1px solid #ccc' }} />
                  </label>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>${carTierPricePreview?.toFixed(2)}</div>
                </div>
              )}
              {event.collectEmergencyContact && (
                <div style={{ marginBottom: 16, display: 'flex', gap: 12 }}>
                  <input
                    placeholder="Emergency contact name"
                    value={emergencyContactName}
                    onChange={(e) => setEmergencyContactName(e.target.value)}
                    style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                  <input
                    placeholder="Emergency contact phone"
                    value={emergencyContactPhone}
                    onChange={(e) => setEmergencyContactPhone(e.target.value)}
                    style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>Coming to the Saturday asado? Meat or veg?</label>
                <div style={{ display: 'flex', gap: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                    <input type="radio" name="dietary" checked={dietaryPreference === 'meat'} onChange={() => setDietaryPreference('meat')} /> Meat
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                    <input type="radio" name="dietary" checked={dietaryPreference === 'veg'} onChange={() => setDietaryPreference('veg')} /> Veg
                  </label>
                </div>
              </div>
              <button onClick={startCardSetup} style={{ padding: '12px 24px', fontSize: 16, cursor: 'pointer' }}>
                {event.maxCapacity && progress && progress.confirmedCount >= event.maxCapacity
                  ? 'Join the waitlist'
                  : isImmediate
                    ? `Sign up & pay${carTierPricePreview != null ? ` $${carTierPricePreview.toFixed(2)}` : ''}`
                    : "Sign up — you won't be charged yet"}
              </button>
            </>
          ) : stripePromise && elementsOptions ? (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <CardSetupForm
                onSaved={completeSignup}
                saving={savingCard}
                setSaving={setSavingCard}
                setError={setError}
                customer={customer}
                submitLabel={isImmediate ? `Pay${carTierPricePreview != null ? ` $${carTierPricePreview.toFixed(2)}` : ''} now` : undefined}
                savingLabel={isImmediate ? 'Charging your card...' : undefined}
              />
            </Elements>
          ) : (
            <p>Loading payment form...</p>
          )}
        </>
      ) : (
        <>
          {/* ---- Command Deck: icon tab bar + one content well ---- */}
          <div style={deckTabsWrap}>
            {TAB_DEFS.map((tab) => (
              <div key={tab.id} onClick={() => setActiveTab(tab.id)} style={deckTabBtn(activeTab === tab.id)}>
                <div style={deckTabIcon}>{tab.ic}</div>
                <div style={deckTabLabel(activeTab === tab.id)}>{tab.label}</div>
                {TAB_BADGES[tab.id] && <div style={deckTabBadge}>{TAB_BADGES[tab.id]}</div>}
              </div>
            ))}
          </div>

          <div style={deckWell}>
            {activeTab === 'overview' && (
              <div>
                {renderOverviewInfo()}
                {myReferralLink && (
                  <div style={{ marginTop: 20 }}>
                    <h3 style={sectionHeading}>Invite friends</h3>
                    <p style={sectionSub}>Share your personal link — we track who invited who so we can keep this a positive space.</p>
                    <input
                      readOnly
                      value={myReferralLink}
                      onClick={(e) => e.target.select()}
                      style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                    />
                  </div>
                )}
              </div>
            )}

            {activeTab === 'organisers' && (
              <div>
                <h3 style={sectionHeading}>Organisers</h3>
                <p style={sectionSub}>Pitch in and run a piece of the weekend.</p>
                {roles.length === 0 ? (
                  <p style={{ fontSize: 14, color: '#888' }}>No roles set up yet.</p>
                ) : (
                  roles.map((role) => (
                    <div key={role.id} style={cardStyle}>
                      <strong>{role.name}</strong>
                      {role.description && <p style={{ margin: '4px 0', color: '#666' }}>{role.description}</p>}
                      <p style={{ margin: '4px 0' }}>
                        Leader: {role.leader ? `${role.leader.firstName} ${role.leader.lastName}` : <em>unclaimed</em>}
                      </p>
                      {role.helpers?.length > 0 && (
                        <p style={{ margin: '4px 0' }}>
                          Helpers: {role.helpers.map((h) => `${h.customer.firstName} ${h.customer.lastName}`).join(', ')}
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        {!role.leader && (
                          <button onClick={() => handleClaimLeader(role.id)} style={{ padding: '6px 12px', cursor: 'pointer' }}>Become leader</button>
                        )}
                        <button onClick={() => handleJoinRole(role.id)} style={{ padding: '6px 12px', cursor: 'pointer' }}>Join as helper</button>
                        <button onClick={() => handleLeaveRole(role.id)} style={{ padding: '6px 12px', cursor: 'pointer' }}>Leave</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'addons' && (
              <div>
                <h3 style={sectionHeading}>Add-ons</h3>
                <p style={sectionSub}>Group-buys — chip in and split the cost.</p>
                {addOns.map((addOn) => {
                  const perShareLitres = addOn.minSharePerPerson;
                  const yieldUnit = (addOn.unitYieldLabel || '').replace(/^[\d.]+/, '').trim();
                  return (
                    <div key={addOn.id} style={cardStyle}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <strong>{addOn.name}</strong>
                        {addOn.status === 'pending_approval' && <span style={{ fontSize: 12, color: '#f57c00' }}>(pending approval)</span>}
                      </div>
                      {addOn.description && (
                        <div style={{ margin: '4px 0' }}>
                          <button
                            type="button"
                            onClick={() => setExpandedAddOnId(expandedAddOnId === addOn.id ? null : addOn.id)}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: '#666', textDecoration: 'underline' }}
                          >
                            {expandedAddOnId === addOn.id ? 'Hide details' : 'Details'}
                          </button>
                          {expandedAddOnId === addOn.id && (
                            <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>{addOn.description}</p>
                          )}
                        </div>
                      )}
                      <p style={{ margin: '4px 0', fontWeight: 600 }}>
                        ${addOn.pricing?.pricePerShare?.toFixed(2)} = {perShareLitres}{yieldUnit}
                        <span style={{ fontWeight: 400, color: '#888' }}> · fits ~{addOn.capacityPerUnit} people/unit · {addOn.pricing?.totalShares ?? 0} shares in ({addOn.pricing?.unitsNeeded ?? 1} unit{(addOn.pricing?.unitsNeeded ?? 1) > 1 ? 's' : ''})</span>
                      </p>
                      {addOn.participants?.length > 0 && (
                        <p style={{ margin: '4px 0', fontSize: 13, color: '#666' }}>
                          In: {addOn.participants.map((p) => `${p.name} (${p.quantity})`).join(', ')}
                        </p>
                      )}
                      {addOn.status === 'active' && (
                        <button onClick={() => handleAddOnSignup(addOn.id, 1)} style={{ padding: '8px 16px', cursor: 'pointer', marginTop: 6 }}>
                          Join in (1 share)
                        </button>
                      )}
                    </div>
                  );
                })}

                <div style={{ padding: 12, background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0', marginTop: 14 }}>
                  <strong style={{ fontSize: 14 }}>Propose your own add-on</strong>
                  <p style={{ margin: '4px 0 10px', fontSize: 13, color: '#666' }}>The organizer will need to approve it before others can join.</p>
                  <input
                    placeholder="Name (e.g. Coffee run)"
                    value={proposedAddOnForm.name}
                    onChange={(e) => setProposedAddOnForm((p) => ({ ...p, name: e.target.value }))}
                    style={{ width: '100%', padding: 8, marginBottom: 8, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <input
                      placeholder="Unit cost ($)"
                      type="number"
                      value={proposedAddOnForm.unitCost}
                      onChange={(e) => setProposedAddOnForm((p) => ({ ...p, unitCost: e.target.value }))}
                      style={{ flex: 1, minWidth: 90, padding: 8, borderRadius: 6, border: '1px solid #ccc' }}
                    />
                    <input
                      placeholder="Unit yield (e.g. 10)"
                      type="number"
                      value={proposedAddOnForm.unitYield}
                      onChange={(e) => setProposedAddOnForm((p) => ({ ...p, unitYield: e.target.value }))}
                      style={{ flex: 1, minWidth: 90, padding: 8, borderRadius: 6, border: '1px solid #ccc' }}
                    />
                    <input
                      placeholder="Min share/person"
                      type="number"
                      value={proposedAddOnForm.minSharePerPerson}
                      onChange={(e) => setProposedAddOnForm((p) => ({ ...p, minSharePerPerson: e.target.value }))}
                      style={{ flex: 1, minWidth: 90, padding: 8, borderRadius: 6, border: '1px solid #ccc' }}
                    />
                  </div>
                  <button onClick={handleProposeAddOn} disabled={proposingAddOn} style={{ padding: '8px 16px', cursor: 'pointer' }}>
                    {proposingAddOn ? 'Submitting...' : 'Propose add-on'}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'gear' && (
              <div>
                <h3 style={sectionHeading}>Gear</h3>
                <p style={sectionSub}>For the communal area (Sites 6 & 7). Multiple people can offer the same thing — they all show up under it.</p>
                {renderNeedOfferBoard(gearItems, 'gear', 'Nothing needed yet.', 'I have this', 'Confirm offer')}
              </div>
            )}

            {activeTab === 'food' && (
              <div>
                <h3 style={sectionHeading}>Food & snacks</h3>
                <p style={sectionSub}>
                  Sharing meals is a big part of how we connect and communicate as a group. Your $60 covers
                  food for the weekend — this is just the wishlist. Add what you'd love to eat at camp.
                </p>
                {renderWishlistBoard(foodItems, 'snacks', 'Nothing on the list yet — start it off below.')}
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h3 style={sectionHeading}>Gallery</h3>
                <p style={sectionSub}>Share photos and videos from the event — anyone can grab a copy for themselves.</p>

                <div style={{ ...cardStyle, marginBottom: 14 }}>
                  <input
                    type="file"
                    accept="image/*,video/*"
                    onChange={(e) => setGalleryUploadFile(e.target.files?.[0] || null)}
                    style={{ fontSize: 13 }}
                  />
                  {galleryUploadFile && (
                    <div style={{ marginTop: 8 }}>
                      <input
                        placeholder="Caption (optional)"
                        value={galleryCaption}
                        onChange={(e) => setGalleryCaption(e.target.value)}
                        style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', fontSize: 13, marginBottom: 8 }}
                      />
                      <button onClick={handleUploadGalleryPhoto} disabled={galleryUploading} style={{ padding: '8px 16px', cursor: 'pointer' }}>
                        {galleryUploading ? 'Sharing...' : 'Share to gallery'}
                      </button>
                    </div>
                  )}
                </div>

                {galleryPhotos.length === 0 ? (
                  <p style={{ fontSize: 14, color: '#888' }}>Nothing shared yet — be the first!</p>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
                    {galleryPhotos.map((photo) => {
                      const isVideo = /\.(mp4|mov|webm|m4v)$/i.test(photo.photoUrl);
                      const isMine = photo.uploadedByCustomer?.id === customer?.id;
                      return (
                        <div key={photo.id} style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                          {isVideo ? (
                            <video src={photo.photoUrl} controls style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }} />
                          ) : (
                            <img src={photo.photoUrl} alt={photo.caption || ''} style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }} />
                          )}
                          <div style={{ padding: 6 }}>
                            {photo.caption && <p style={{ margin: 0, fontSize: 12 }}>{photo.caption}</p>}
                            <p style={{ margin: '2px 0 4px', fontSize: 11, color: '#888' }}>{photo.uploadedByCustomer?.firstName}</p>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <a href={photo.photoUrl} download target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>Download</a>
                              {isMine && (
                                <button
                                  onClick={() => handleDeleteGalleryPhoto(photo.id)}
                                  style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 11, padding: 0 }}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'mystuff' && (
              <div>
                <h3 style={sectionHeading}>My stuff</h3>
                <p style={sectionSub}>Manage your spot, your crew, and everything you've put your hand up for.</p>

                {signupResult.waitlisted ? (
                  <div style={{ ...cardStyle, background: '#fff3cd', borderColor: '#ffc107' }}>
                    <strong>You're on the waitlist!</strong>
                    <p style={{ margin: '4px 0 0' }}>We'll charge your card and confirm your spot if the event expands.</p>
                  </div>
                ) : signupResult.data?.status === 'payment_failed' ? (
                  <div style={{ ...cardStyle, background: '#fdecea', borderColor: '#dc3545' }}>
                    <strong>Your payment didn't go through</strong>
                    <p style={{ margin: '4px 0 8px' }}>The guest list is locked in, but we couldn't charge the card on file. Update your card to secure your spot.</p>
                    {!retryClientSecret ? (
                      <button onClick={startPaymentRetry} style={{ padding: '10px 20px', cursor: 'pointer' }}>Update card</button>
                    ) : stripePromise ? (
                      <Elements stripe={stripePromise} options={{ clientSecret: retryClientSecret }}>
                        <CardSetupForm onSaved={completeRetry} saving={retrySaving} setSaving={setRetrySaving} setError={setError} customer={customer} />
                      </Elements>
                    ) : (
                      <p>Loading payment form...</p>
                    )}
                  </div>
                ) : signupResult.data?.status === 'valid' ? (
                  <div style={{ ...cardStyle, background: '#e8f5e9', borderColor: '#28a745' }}>
                    <strong>Confirmed and charged — see you there!</strong>
                  </div>
                ) : (
                  <div style={{ ...cardStyle, background: '#e8f5e9', borderColor: '#28a745' }}>
                    <strong>Confirmed to attend</strong>
                    <p style={{ margin: '4px 0 8px' }}>
                      {isImmediate ? 'Your card is being charged — refresh in a moment if this sticks around.' : 'Card saved, not charged until we hit the signup threshold.'}
                    </p>
                    <button onClick={handleCancelSignup} disabled={cancelling} style={{ padding: '8px 14px', fontSize: 13, cursor: 'pointer', background: 'transparent', border: '1px solid #dc3545', color: '#dc3545', borderRadius: 6 }}>
                      {cancelling ? 'Cancelling...' : 'Cancel my spot'}
                    </button>
                  </div>
                )}

                <div style={cardStyle}>
                  <strong>Meat or veg</strong>
                  <p style={{ margin: '4px 0 8px', fontSize: 13, color: '#666' }}>For the Saturday asado — change it any time before then.</p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {['meat', 'veg'].map((opt) => (
                      <button
                        key={opt}
                        onClick={() => handleUpdateDietary(opt)}
                        disabled={updatingDietary}
                        style={{
                          padding: '6px 14px', borderRadius: 20, cursor: 'pointer', textTransform: 'capitalize',
                          border: signupResult.data?.dietaryPreference === opt ? '1px solid #333' : '1px solid #ccc',
                          background: signupResult.data?.dietaryPreference === opt ? '#333' : '#fff',
                          color: signupResult.data?.dietaryPreference === opt ? '#fff' : '#333',
                        }}
                      >
                        {opt}
                      </button>
                    ))}
                    {signupResult.data?.dietaryPreference && (
                      <button
                        onClick={() => handleUpdateDietary(null)}
                        disabled={updatingDietary}
                        style={{ padding: '6px 14px', borderRadius: 20, cursor: 'pointer', border: '1px solid #ccc', background: '#fff', color: '#888' }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                <div style={cardStyle}>
                  <strong>Transport</strong>
                  <p style={{ margin: '4px 0 8px', fontSize: 13, color: '#666' }}>
                    What we need is cars, and who's in each one. Bringing a car with spare seats? Post it, and anyone can claim one.
                  </p>
                  {renderNeedOfferBoard(
                    carItems,
                    'car',
                    'No cars posted yet.',
                    'Claim a seat',
                    "I'm in",
                    "Bringing a car? e.g. \"Zee's car — 2 spare seats\"",
                    newCarName,
                    setNewCarName
                  )}
                </div>

                <div style={cardStyle}>
                  <strong>Sleeping</strong>
                  <p style={{ margin: '4px 0 8px', fontSize: 13, color: '#666' }}>
                    Tent, car or van — got room to spare wherever you're sleeping? Post it, and anyone can claim a spot.
                  </p>
                  {renderNeedOfferBoard(
                    tentItems,
                    'tent',
                    'Nothing posted yet.',
                    'Claim a spot',
                    "I'm in",
                    "Got room? e.g. \"Zee's tent — 2 spare spots\" or \"Zee's van — 1 spare spot\"",
                    newTentName,
                    setNewTentName
                  )}
                </div>

                <div style={cardStyle}>
                  <strong>What I've offered</strong>
                  {myOfferedItems.length === 0 ? (
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#888' }}>Nothing yet — check the Gear or Food tabs.</p>
                  ) : (
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>
                      {myOfferedItems.map((it) => it.name).join(', ')}
                    </p>
                  )}
                </div>

                <div style={cardStyle}>
                  <strong>Add-ons I've joined</strong>
                  {myAddOns.length === 0 ? (
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#888' }}>Nothing yet — check the Add-ons tab.</p>
                  ) : (
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>
                      {myAddOns.map((a) => a.name).join(', ')}
                    </p>
                  )}
                </div>

                {myReferralLink && (
                  <div style={cardStyle}>
                    <strong>Invite friends</strong>
                    <p style={{ margin: '4px 0 8px', fontSize: 13, color: '#666' }}>We track who invited who so we can keep this a positive space.</p>
                    <input
                      readOnly
                      value={myReferralLink}
                      onClick={(e) => e.target.select()}
                      style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}
                    />
                  </div>
                )}
              </div>
            )}

            {activeTab === 'chat' && (
              <div>
                <h3 style={sectionHeading}>Group chat</h3>
                <p style={sectionSub}>One shared space — filter by topic below, or view everything under "All".</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                  <button
                    onClick={() => setSelectedTopicId(null)}
                    style={{
                      padding: '6px 14px', borderRadius: 20, cursor: 'pointer', border: '1px solid #ccc',
                      background: selectedTopicId === null ? '#333' : '#fff',
                      color: selectedTopicId === null ? '#fff' : '#333',
                    }}
                  >
                    All
                  </button>
                  {chatTopics.map((topic) => (
                    <button
                      key={topic.id}
                      onClick={() => setSelectedTopicId(topic.id)}
                      style={{
                        padding: '6px 14px', borderRadius: 20, cursor: 'pointer', border: '1px solid #ccc',
                        background: selectedTopicId === topic.id ? '#333' : '#fff',
                        color: selectedTopicId === topic.id ? '#fff' : '#333',
                      }}
                    >
                      {topic.name}
                    </button>
                  ))}
                </div>
                <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 8, padding: 10, marginBottom: 10, background: '#fff' }}>
                  {chatLoadError ? (
                    <p style={{ fontSize: 14, color: 'crimson' }}>Couldn't load chat: {chatLoadError}</p>
                  ) : chatMessages.length === 0 ? (
                    <p style={{ fontSize: 14, color: '#888' }}>No messages yet — say hi!</p>
                  ) : (
                    chatMessages.map((msg) => (
                      <div key={msg.id} style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 13 }}>
                          <strong>{msg.isAdmin ? 'Organizer' : msg.isStaff ? 'Staff' : `${msg.customer?.firstName || ''} ${msg.customer?.lastName || ''}`}</strong>
                          {msg.topic?.name && <span style={{ color: '#888', marginLeft: 6 }}>#{msg.topic.name}</span>}
                          {msg.status === 'pending' && <span style={{ color: '#f57c00', marginLeft: 6 }}>(pending approval)</span>}
                        </div>
                        <p style={{ margin: '2px 0' }}>{msg.content}</p>
                        {msg.photoUrl && <img src={msg.photoUrl} alt="attachment" style={{ maxWidth: 160, borderRadius: 6 }} />}
                      </div>
                    ))
                  )}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={showItemOffer} onChange={(e) => setShowItemOffer(e.target.checked)} />
                  Offering gear/an item with this message
                </label>
                {showItemOffer && (
                  <input
                    placeholder="Item name (e.g. Generator, big pot)"
                    value={chatItemName}
                    onChange={(e) => setChatItemName(e.target.value)}
                    style={{ width: '100%', padding: 10, marginBottom: 8, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input
                    placeholder={selectedTopicId ? `Message in #${chatTopics.find((t) => t.id === selectedTopicId)?.name || ''}...` : 'Message...'}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    style={{ flex: 1, minWidth: 140, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                  {showItemOffer && (
                    <input type="file" accept="image/*" onChange={(e) => setChatPhotoFile(e.target.files?.[0] || null)} />
                  )}
                  <button onClick={handleSendChatMessage} disabled={sendingChat} style={{ padding: '10px 16px', cursor: 'pointer' }}>
                    {sendingChat ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
